// Person-versus-organisation entry prompts for the admin user form (COMPANY-SETTINGS-ADMIN §7).
//
// WHY THIS EXISTS. Two user records were found role-tagged as Student when they are
// organisations (HM Air and one other), corrected by Ben 2026-09-04. Two wrong in the same
// direction is a pattern, not a coincidence, and correcting them does not stop the third.
//
// ⚠ THESE WARN AND NEVER BLOCK, AND THAT IS NOT A UI DETAIL. A sole trader with a company name
// is a real person who may well be a training student. "Has a company name" is fine as an
// entry-time prompt and WRONG as a gate — it must never become the discriminator for anything
// with legal weight, such as the student-module terms exemption, which keys off the person's
// system role. Nothing in this module returns a reason to refuse a save; the caller shows the
// text, the operator confirms, and the record is written exactly as typed.
//
// A PURE MODULE, no React, so the verify gate can assert the copy and the trigger conditions
// directly — the same reason buildUserFormBody was extracted on 2026-08-27.

export interface IdentityInput {
  first_name: string;
  last_name: string;
  company: string;
  roles: string[];
}

// Roles that mean "an organisation" rather than a person.
const ORGANISATION_ROLES = new Set(['client']);

export function identityPrompts(input: IdentityInput): string[] {
  const first = input.first_name.trim();
  const last = input.last_name.trim();
  const company = input.company.trim();
  const hasPerson = !!(first || last);
  const isOrganisationRole = input.roles.some((r) => ORGANISATION_ROLES.has(r));
  const prompts: string[] = [];

  // Case 1 — a company name and no person name, on a non-Client role. This is the shape the
  // two miscategorised records had.
  if (company && !hasPerson && !isOrganisationRole) {
    prompts.push('This looks like an organisation. Did you mean to set the role to Client?');
  }

  // Case 2 — a person name AND a company. schema.prisma:637-638 records the rule: "first+last
  // form a person; company is for client/organisation records. Person name wins for full_name
  // when both." So the record will DISPLAY as the person and the company is decoration.
  //
  // ⚠ The copy states the CONSEQUENCE rather than raising a doubt. A prompt that says what
  // will happen gets acted on; one that only expresses unease gets clicked through. The names
  // are interpolated so the operator reads their own data back, not an abstraction.
  if (company && hasPerson) {
    const personName = [first, last].filter(Boolean).join(' ');
    prompts.push(`This will display as ${personName}, not ${company}. Is that what you want?`);
  }

  return prompts;
}
